import { normalizePhoneNumber } from "../../../lib/phone-number";

export interface ContactField {
  key: string;
  label: string;
  required: boolean;
}

export interface ContactRowError {
  row: number;
  field: string;
  reason: string;
}

export interface ContactNormalizationResult {
  normalized: Record<string, string>[];
  rejected: Record<string, string>[];
  errors: ContactRowError[];
  deduped: number;
}

const PHONE_KEYS = new Set([
  "phone",
  "phonenumber",
  "number",
  "mobile",
  "mobilenumber",
  "contact",
]);

const normalizeKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
const hasValue = (value: unknown) => String(value ?? "").trim().length > 0;

const isPhoneKey = (key: string) => {
  if (key.endsWith("_cc")) return false;
  const normalized = normalizeKey(key);
  return PHONE_KEYS.has(normalized) || normalized.includes("phone") || normalized.includes("mobile");
};

const isNameKey = (key: string) => normalizeKey(key) === "name";
const isContactIdKey = (key: string) => ["id", "contactid"].includes(normalizeKey(key));

function readMappedValue(
  row: Record<string, string>,
  fieldKey: string,
  mapping: Record<string, string>,
) {
  const sourceKey = mapping[fieldKey] || fieldKey;
  return String(row[sourceKey] ?? "").trim();
}

function findValue(row: Record<string, string>, predicate: (key: string) => boolean) {
  const entry = Object.entries(row).find(([key, value]) => predicate(key) && hasValue(value));
  return entry ? String(entry[1]).trim() : "";
}

// Phone format validity and duplicate detection are NOT checked here — the
// backend is the single source of truth for both (normalize_phone_number_any_country
// in schedules/service.py, hit via POST /schedules/validate-contacts and again
// at Launch Batch). This function only checks required fields and best-effort
// formats the phone with its country code before handing rows to the server.
export function normalizeContactRows({
  rows,
  fields,
  mapping,
  sourceIndices,
}: {
  rows: Record<string, string>[];
  fields: ContactField[];
  mapping: Record<string, string>;
  sourceIndices?: number[];
}): ContactNormalizationResult {
  const normalized: Record<string, string>[] = [];
  const rejected: Record<string, string>[] = [];
  const errors: ContactRowError[] = [];
  const deduped = 0;

  const buildContact = (
    row: Record<string, string>,
    mapped: Record<string, string>,
    phone: string,
    sourceRow: number,
    rejectionReason?: string,
  ) => {
    const contact: Record<string, string> = {};
    Object.entries(row).forEach(([key, value]) => {
      if (hasValue(value) && !key.endsWith("_cc") && !isPhoneKey(key) && !isNameKey(key) && !isContactIdKey(key)) {
        contact[key] = String(value).trim();
      }
    });
    Object.entries(mapped).forEach(([key, value]) => {
      if (hasValue(value) && !isPhoneKey(key) && !isNameKey(key) && !isContactIdKey(key)) {
        contact[key] = value;
      }
    });
    const phoneTail = (phone.replace(/\D/g, "").slice(-4) || "0000");
    contact.contact_id =
      findValue(row, isContactIdKey)
      || `cont-${String(sourceRow + 1).padStart(4, "0")}-${phoneTail}`;
    contact.name =
      Object.entries(mapped).find(([key, value]) => isNameKey(key) && hasValue(value))?.[1]
      || findValue(row, isNameKey);
    contact.phone_number = phone;
    contact.source_row = String(sourceRow + 1);
    if (rejectionReason) {
      contact.rejection_reason = rejectionReason;
      contact.status = "rejected";
    }
    return contact;
  };

  rows.forEach((row, index) => {
    const sourceRow = sourceIndices?.[index] ?? index;
    const mapped = Object.fromEntries(
      fields.map((field) => [field.key, readMappedValue(row, field.key, mapping)]),
    );
    const mappedPhoneEntry = Object.entries(mapped).find(([key, value]) => isPhoneKey(key) && hasValue(value));
    const rowPhoneEntry = Object.entries(row).find(([key, value]) => isPhoneKey(key) && hasValue(value));
    const rawPhone =
      mappedPhoneEntry?.[1]
      || rowPhoneEntry?.[1]
      || Object.values(row).find((value) => /^[+]?[0-9\s\-().]{10,16}$/.test(String(value).trim()))
      || "";

    const phoneKey = mappedPhoneEntry?.[0] || rowPhoneEntry?.[0] || "phone_number";
    const defaultCc = row[`${phoneKey}_cc`] || row["phone_number_cc"] || "+91";
    // Best-effort formatting only; an unformattable value is still sent to the
    // backend as-is (trimmed) so the server's validator makes the real call.
    const phone = normalizePhoneNumber(rawPhone, defaultCc) || String(rawPhone || "").trim();

    const missing = fields.filter((field) => field.required && !hasValue(mapped[field.key]));
    const missingNonPhone = missing.filter((field) => !isPhoneKey(field.key));
    if (missingNonPhone.length > 0) {
      const reason = `Missing required field: ${missingNonPhone.map((f) => f.label || f.key).join(", ")}`;
      missingNonPhone.forEach((field) => errors.push({ row: sourceRow, field: field.key, reason: "Required value is missing" }));
      rejected.push(buildContact(row, mapped, phone || "invalid", sourceRow, reason));
      return;
    }

    normalized.push(buildContact(row, mapped, phone, sourceRow));
  });

  return { normalized, rejected, errors, deduped };
}
